(function () {
  'use strict';

  // Storefront checkout through an app-priced draft order: validates Zapiet pickup/delivery details, saves the
  // cart form, creates the draft order, and clears the cart once its checkout is paid.
  // Created by aboffs-central-pricing.js, which passes in the helpers it shares.

  var DRAFT_CHECKOUT_PATH = '/apps/aboffs-minion/draft-checkout';
  var DRAFT_CHECKOUT_STATUS_PATH = '/apps/aboffs-minion/draft-checkout-status';
  var PENDING_DRAFT_CHECKOUT_KEY = 'aboffs:pendingDraftCheckout';
  var PENDING_DRAFT_CHECKOUT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  var DRAFT_INTENT_WINDOW_MS = 5000;
  var ZAPIET_CHECKOUT_METHOD_ATTR = 'Checkout-Method';

  function create(deps) {
    var appUrl = deps.appUrl;
    var fetchCartJson = deps.fetchCartJson;
    var discountCodesFromCart = deps.discountCodesFromCart;
    var isCartTemplateInteractionTarget = deps.isCartTemplateInteractionTarget;

    var draftCheckoutIntent = null;
    var resetInflightDraftCheckout = null;
    var installSubmitPatchDone = false;

    function findTosCheckbox(root) {
      if (!root) return null;
      return root.querySelector('.tos_agree[required], input.tos_agree[required]');
    }

    function focusZapietWidget() {
      var widget = document.getElementById('storePickupApp');
      if (!widget) return;
      try {
        widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        widget.scrollIntoView();
      }
      var focusable = widget.querySelector('input, select, textarea, button, [tabindex]');
      if (focusable && focusable.focus) focusable.focus();
    }

    function showZapietCheckoutNotice(message) {
      var text = String(message || '').trim();
      if (!text) return;
      var notice = document.getElementById('aboffs-zapiet-notice');
      if (notice) {
        notice.textContent = text;
        notice.hidden = false;
        notice.style.display = '';
        return;
      }
      alert(text);
    }

    function clearZapietCheckoutNotice() {
      var notice = document.getElementById('aboffs-zapiet-notice');
      if (!notice) return;
      notice.textContent = '';
      notice.hidden = true;
      notice.style.display = 'none';
    }

    function isSafeCheckoutUrl(url) {
      if (!url || typeof url !== 'string') return false;
      try {
        var parsed = new URL(url, window.location.origin);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
        return true;
      } catch (e) {
        return false;
      }
    }

    function isCheckoutSubmitButton(el) {
      if (!el || !el.matches) return false;
      if (el.matches('[data-draft-checkout]')) return true;
      if (el.matches('button[name="checkout"], input[name="checkout"], #checkout')) return true;
      return false;
    }

    function isDraftEligibleForm(form) {
      return !!(form && form.id === 'cart_form');
    }

    var CART_UPDATE_PATH = '/cart';

    function resetCartFormActionForUpdate(form) {
      if (!form || form.id !== 'cart_form') return;
      if (form.getAttribute('action') !== CART_UPDATE_PATH) {
        form.setAttribute('action', CART_UPDATE_PATH);
      }
    }

    function isCartTemplateUpdateInteraction(target) {
      if (!target || !target.closest) return false;
      if (!isCartTemplateInteractionTarget(target)) return false;
      return !!target.closest('[data-func], [data-remove-item], [data-cart-quantity-input]');
    }

    function handleMiniCartCheckoutRedirect(button) {
      if (!button) return;
      var form = button.closest ? button.closest('form') : null;
      var tos = findTosCheckbox(form || document);
      if (tos && tos.required && !tos.checked) {
        alert('Please agree to the terms of service.');
        tos.focus();
        return;
      }
      window.location.href = '/cart';
    }

    function rememberDraftCheckoutIntent(button) {
      draftCheckoutIntent = {
        button: button || null,
        at: Date.now(),
      };
    }

    function getRecentDraftCheckoutIntent() {
      if (!draftCheckoutIntent) return null;
      if (Date.now() - draftCheckoutIntent.at > DRAFT_INTENT_WINDOW_MS) {
        draftCheckoutIntent = null;
        return null;
      }
      return draftCheckoutIntent;
    }

    function clearDraftCheckoutIntent() {
      draftCheckoutIntent = null;
    }

    function isExplicitCheckoutSubmission(submitter) {
      return !!(submitter && isCheckoutSubmitButton(submitter));
    }

    function resolveCheckoutButton(form, submitter) {
      if (submitter && isCheckoutSubmitButton(submitter)) return submitter;
      var intent = getRecentDraftCheckoutIntent();
      if (intent && intent.button && intent.button.isConnected) return intent.button;
      if (!form || !form.querySelector) return null;
      return (
        form.querySelector('[data-draft-checkout]') ||
        form.querySelector('button[name="checkout"], input[name="checkout"], #checkout')
      );
    }

    function installDraftCheckoutSubmitPatch() {
      if (installSubmitPatchDone) return;
      installSubmitPatchDone = true;
      if (typeof HTMLFormElement === 'undefined' || !HTMLFormElement.prototype) return;

      if (typeof window !== 'undefined' && typeof window.aboffsNativeSubmit !== 'function') {
        window.aboffsNativeSubmit = HTMLFormElement.prototype.submit;
      }
      var nativeSubmit =
        typeof window !== 'undefined' && typeof window.aboffsNativeSubmit === 'function'
          ? window.aboffsNativeSubmit
          : HTMLFormElement.prototype.submit;
      HTMLFormElement.prototype.submit = function () {
        var form = this;
        if (!form || !form.querySelector || form.id !== 'cart_form') {
          return nativeSubmit.apply(form, arguments);
        }

        var intent = getRecentDraftCheckoutIntent();
        if (!intent) {
          resetCartFormActionForUpdate(form);
          return nativeSubmit.apply(form, arguments);
        }

        var button = resolveCheckoutButton(form, null);
        if (!button) {
          return nativeSubmit.apply(form, arguments);
        }

        startDraftCheckoutFlow(button, form);
      };
    }

    function findCartNote(root) {
      var scope = root || document;
      var noteEl =
        scope.querySelector('textarea[name="note"]') ||
        document.querySelector('textarea[name="note"]') ||
        document.getElementById('note');
      return noteEl && noteEl.value ? String(noteEl.value).trim() : '';
    }

    function readCartAttributesFromForm(root) {
      var scope = root || document;
      if (!scope || !scope.querySelectorAll) return {};
      var attrs = {};
      var fields = scope.querySelectorAll(
        'input[name^="attributes["], textarea[name^="attributes["], select[name^="attributes["]',
      );
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        if (!field || field.disabled || typeof field.name !== 'string') continue;
        var match = field.name.match(/^attributes\[(.+)\]$/);
        if (!match || !match[1]) continue;
        attrs[match[1]] = field.value == null ? '' : String(field.value);
      }
      return attrs;
    }

    // Saves the note, attributes and quantities the cart form holds but the cart doesn't, sending only
    // what differs: Zapiet restarts its widget (blanking the chosen date and time) after any
    // /cart/update carrying `updates`, so an unchanged cart must not post one. Resolves true when it saved.
    function syncCartFormState(form, cart) {
      if (!form || !form.tagName || String(form.tagName).toLowerCase() !== 'form') {
        return Promise.resolve(false);
      }
      var formData = new FormData(form);
      var payload = new URLSearchParams();
      var quantities = [];
      var hasChanges = false;
      var cartAttributes = (cart && cart.attributes) || {};
      formData.forEach(function (value, key) {
        if (typeof key !== 'string') return;
        var text = value == null ? '' : String(value);
        if (key === 'updates[]') {
          quantities.push(text);
          return;
        }
        var current;
        if (key === 'note') {
          current = cart && cart.note;
        } else {
          var match = key.match(/^attributes\[(.+)\]$/);
          if (!match) return;
          current = cartAttributes[match[1]];
        }
        if (text === (current == null ? '' : String(current))) return;
        payload.append(key, text);
        hasChanges = true;
      });
      var items = (cart && cart.items) || [];
      var quantitiesChanged =
        quantities.length > 0 &&
        (quantities.length !== items.length ||
          quantities.some(function (quantity, i) {
            return parseInt(quantity, 10) !== items[i].quantity;
          }));
      if (quantitiesChanged) {
        quantities.forEach(function (quantity) {
          payload.append('updates[]', quantity);
        });
        hasChanges = true;
      }
      if (!hasChanges) return Promise.resolve(false);
      return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        credentials: 'same-origin',
        body: payload.toString(),
      }).then(function (response) {
        if (!response.ok) throw new Error('Unable to save your cart. Please try again.');
        return true;
      });
    }

    function zapietCheckoutNotReadyError(message) {
      var err = new Error(message || 'Please complete pickup, delivery, or shipping selection before checkout.');
      err.code = 'ZAPIET_CHECKOUT_REQUIRED';
      return err;
    }

    function validateZapietCheckoutAttributes(attributes) {
      var method = cartAttributeValue(attributes, ZAPIET_CHECKOUT_METHOD_ATTR).toLowerCase();
      if (!method) {
        return {
          ok: false,
          message: 'Please choose pickup, delivery, or shipping above before checkout.',
        };
      }
      if (method === 'pickup') {
        if (!cartAttributeValue(attributes, 'Pickup-Location-Id')) {
          return {
            ok: false,
            message: 'Please select a pickup location before checkout.',
          };
        }
        if (!cartAttributeValue(attributes, 'Pickup-Date') || !cartAttributeValue(attributes, 'Pickup-Time')) {
          return {
            ok: false,
            message: 'Please select a pickup date and time before checkout.',
          };
        }
      } else if (method === 'delivery') {
        if (!cartAttributeValue(attributes, 'Delivery-Location-Id')) {
          return {
            ok: false,
            message: 'Please confirm your delivery address before checkout.',
          };
        }
        if (!cartAttributeValue(attributes, 'Delivery-Date') || !cartAttributeValue(attributes, 'Delivery-Time')) {
          return {
            ok: false,
            message: 'Please select a delivery date and time before checkout.',
          };
        }
      } else if (method === 'shipping') {
        if (!cartAttributeValue(attributes, 'Shipping-Date')) {
          return {
            ok: false,
            message: 'Please select a shipping date before checkout.',
          };
        }
      } else {
        return {
          ok: false,
          message: 'Please complete your delivery method selection before checkout.',
        };
      }
      return { ok: true, method: method };
    }

    function copyLineItemProperties(properties) {
      return properties && typeof properties === 'object' ? Object.assign({}, properties) : {};
    }

    function cartAttributeValue(attributes, key) {
      if (!attributes || typeof attributes !== 'object') return '';
      if (attributes[key] != null && String(attributes[key]).trim()) {
        return String(attributes[key]).trim();
      }
      var target = String(key || '').toLowerCase();
      for (var attrKey in attributes) {
        if (!Object.prototype.hasOwnProperty.call(attributes, attrKey)) continue;
        if (String(attrKey).toLowerCase() !== target) continue;
        var value = attributes[attrKey];
        if (value != null && String(value).trim()) return String(value).trim();
      }
      return '';
    }

    async function createDraftCheckout(sourceForm) {
      await window.AboffsRegion.whenSettled();
      let cart = await fetchCartJson();
      if (await syncCartFormState(sourceForm, cart)) cart = await fetchCartJson();
      if (!cart) throw new Error('Unable to load your cart. Please try again.');
      const attributes = Object.assign({}, cart.attributes || {}, readCartAttributesFromForm(sourceForm || document));
      const validation = validateZapietCheckoutAttributes(attributes);
      if (!validation.ok) throw zapietCheckoutNotReadyError(validation.message);
      if (
        typeof window.__aboffsPickupNoSlotsBlocksCheckout === 'function' &&
        window.__aboffsPickupNoSlotsBlocksCheckout()
      ) {
        throw zapietCheckoutNotReadyError('No pickup times are available for this date. Please choose another date.');
      }
      const revision = window.AboffsRegion.getRevision();
      const state = window.AboffsRegion.stateForCheckout(attributes);
      const lines = (cart.items || []).map(function (item) {
        return {
          variantId: item.variant_id,
          quantity: item.quantity,
          properties: copyLineItemProperties(item.properties || {}),
        };
      });
      if (!lines.length) throw new Error('Your cart is empty');
      const response = await fetch(appUrl(DRAFT_CHECKOUT_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          lines: lines,
          state: state,
          attributes: attributes,
          discountCodes: discountCodesFromCart(cart),
          note: findCartNote(sourceForm || document) || cart.note || '',
        }),
      });
      const data = await response.json();
      if (revision !== window.AboffsRegion.getRevision())
        throw new Error('Your pickup region changed. Please check your prices and try checkout again.');
      if (!response.ok || !data || !data.ok || !data.checkoutUrl) {
        throw new Error((data && data.message) || 'Unable to create checkout. Please try again.');
      }
      rememberPendingDraftCheckout(data.draftOrderId, cart);
      return data.checkoutUrl;
    }

    // Draft order checkouts leave the storefront cart untouched, so the cart is cleared here once the
    // draft order is paid: on the next page load, if the cart still holds what was checked out.
    function draftCheckoutCartSignature(cart) {
      var items = (cart && cart.items) || [];
      return (
        String((cart && cart.token) || '') +
        '|' +
        items
          .map(function (item) {
            return item.variant_id + ':' + item.quantity;
          })
          .join(',')
      );
    }

    function rememberPendingDraftCheckout(draftOrderId, cart) {
      if (!draftOrderId) return;
      try {
        localStorage.setItem(
          PENDING_DRAFT_CHECKOUT_KEY,
          JSON.stringify({ id: String(draftOrderId), cart: draftCheckoutCartSignature(cart), at: Date.now() }),
        );
      } catch (e) {}
    }

    function readPendingDraftCheckout() {
      try {
        return JSON.parse(localStorage.getItem(PENDING_DRAFT_CHECKOUT_KEY) || 'null');
      } catch (e) {
        return null;
      }
    }

    function forgetPendingDraftCheckout() {
      try {
        localStorage.removeItem(PENDING_DRAFT_CHECKOUT_KEY);
      } catch (e) {}
    }

    var pendingDraftCheckoutCheck = null;

    function clearCartIfDraftCheckoutPaid() {
      var pending = readPendingDraftCheckout();
      if (!pending || !pending.id) return;
      if (!(Date.now() - pending.at < PENDING_DRAFT_CHECKOUT_MAX_AGE_MS)) {
        forgetPendingDraftCheckout();
        return;
      }
      if (pendingDraftCheckoutCheck) return;
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      pendingDraftCheckoutCheck = fetchCartJson()
        .then(function (cart) {
          if (!cart) return;
          // The cart was changed or replaced after checkout began; it isn't the one that was paid for.
          if (draftCheckoutCartSignature(cart) !== pending.cart) {
            forgetPendingDraftCheckout();
            return;
          }
          return fetch(appUrl(DRAFT_CHECKOUT_STATUS_PATH + '?id=' + encodeURIComponent(pending.id)), {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            cache: 'no-store',
          })
            .then(function (response) {
              return response.ok ? response.json() : null;
            })
            .then(function (data) {
              if (!data || !data.completed) return;
              return fetch(root + 'cart/clear.js', {
                method: 'POST',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
              }).then(function (response) {
                if (!response.ok) return;
                forgetPendingDraftCheckout();
                // The page was rendered from the paid cart.
                window.location.reload();
              });
            });
        })
        .catch(function (err) {
          console.warn('[aboffs-pricing] Unable to check draft checkout status:', err);
        })
        .then(function () {
          pendingDraftCheckoutCheck = null;
        });
    }

    function startDraftCheckoutFlow(button, providedForm) {
      if (!button) return;
      if (button.getAttribute('data-draft-checkout-inflight') === '1') return;
      clearDraftCheckoutIntent();
      button.setAttribute('data-draft-checkout-inflight', '1');

      var root = providedForm || button.closest('form') || button.closest('[data-cart-form]') || document;
      var tos = findTosCheckbox(root);
      if (tos && tos.required && !tos.checked) {
        alert('Please agree to the terms of service.');
        tos.focus();
        button.removeAttribute('data-draft-checkout-inflight');
        return;
      }

      var originalHtml = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Creating checkout...';
      resetInflightDraftCheckout = function () {
        resetInflightDraftCheckout = null;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = originalHtml;
        button.removeAttribute('data-draft-checkout-inflight');
      };

      createDraftCheckout(root)
        .then(function (checkoutUrl) {
          if (!isSafeCheckoutUrl(checkoutUrl)) {
            throw new Error('Invalid checkout URL from API');
          }
          window.location.href = checkoutUrl;
        })
        .catch(function (err) {
          console.error('[aboffs-pricing] Draft checkout failed:', err);
          showZapietCheckoutNotice((err && err.message) || 'Checkout failed. Please try again.');
          if (err && err.code === 'ZAPIET_CHECKOUT_REQUIRED') focusZapietWidget();
          if (resetInflightDraftCheckout) resetInflightDraftCheckout();
        });
    }

    function installCartFormUpdateGuard() {
      // Zapiet sets #cart_form action to /checkout once pickup is complete. Theme cart
      // qty/remove refreshes submit that form and must stay on /cart.
      document.addEventListener(
        'click',
        function (e) {
          var target =
            e.target && e.target.closest
              ? e.target.closest('[data-func], [data-remove-item], [data-cart-quantity-input]')
              : null;
          if (!isCartTemplateUpdateInteraction(target)) return;
          clearDraftCheckoutIntent();
          resetCartFormActionForUpdate(document.getElementById('cart_form'));
        },
        true,
      );

      document.addEventListener(
        'submit',
        function (e) {
          var form = e.target;
          if (!isDraftEligibleForm(form)) return;
          if (isExplicitCheckoutSubmission(e.submitter || null)) return;
          clearDraftCheckoutIntent();
          resetCartFormActionForUpdate(form);
        },
        true,
      );
    }

    function installDraftCheckoutHandler() {
      installCartFormUpdateGuard();
      installDraftCheckoutSubmitPatch();

      // Going back from checkout can restore this page from the back/forward cache, with the
      // button still disabled on "Creating checkout...".
      window.addEventListener('pageshow', function (e) {
        if (!e.persisted) return;
        if (resetInflightDraftCheckout) resetInflightDraftCheckout();
        clearCartIfDraftCheckoutPaid();
      });
      clearCartIfDraftCheckoutPaid();

      document.addEventListener(
        'click',
        function (e) {
          var redirectTarget = e.target && e.target.closest ? e.target.closest('[data-cart-checkout-redirect]') : null;
          if (redirectTarget) {
            e.preventDefault();
            handleMiniCartCheckoutRedirect(redirectTarget);
            return;
          }

          var target =
            e.target && e.target.closest
              ? e.target.closest('button, input[type="submit"], #checkout, [data-draft-checkout]')
              : null;
          if (!target || !isCheckoutSubmitButton(target)) return;
          var form = target.form || (target.closest ? target.closest('form') : null);
          if (!isDraftEligibleForm(form)) return;
          rememberDraftCheckoutIntent(target);
        },
        true,
      );

      // Run on bubbling submit so Zapiet handlers can validate/mutate first.
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.querySelector) return;
        if (e.defaultPrevented) return;
        if (!isDraftEligibleForm(form)) return;

        var submitter = e.submitter || null;
        // Cart qty/remove uses jQuery .submit(), which fires this event without a
        // submitter — must not be treated as checkout.
        if (!isExplicitCheckoutSubmission(submitter)) return;

        if (
          typeof window.__aboffsPickupNoSlotsBlocksCheckout === 'function' &&
          window.__aboffsPickupNoSlotsBlocksCheckout()
        ) {
          e.preventDefault();
          showZapietCheckoutNotice('No pickup times are available for this date. Please choose another date.');
          focusZapietWidget();
          return;
        }

        if (submitter && isCheckoutSubmitButton(submitter)) {
          rememberDraftCheckoutIntent(submitter);
        }

        var button = resolveCheckoutButton(form, submitter);
        if (!button) return;

        clearZapietCheckoutNotice();
        e.preventDefault();
        startDraftCheckoutFlow(button, form);
      });
    }

    return Object.freeze({
      install: installDraftCheckoutHandler,
      clearIntent: clearDraftCheckoutIntent,
      showNotice: showZapietCheckoutNotice,
    });
  }

  window.AboffsDraftCheckout = Object.freeze({ create: create });
})();
